import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Download, ArrowUpAZ, ArrowDownAZ } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { downloadCsvRows } from "../lib/csvImport";
import { sortRecords } from "../lib/sortRecords";
import { AssetImport } from "../components/AssetImport";
import { AssetForm } from "../components/AssetForm";
import { AssetFilters, EMPTY_ASSET_FILTERS, filterAssets } from "../components/AssetFilters";
import { AssetRow, ASSET_COLUMNS } from "../components/AssetRow";
import { PageHeader, DataTable, SelectInput, ActionButton, EmptyState } from "../components/common";

const SECTIONS = [
  { title: "Identity", fields: [
    { key: "fixed_asset_id", label: "Fixed Asset ID" },
    { key: "asset_name", label: "Asset Name", required: true },
    { key: "alt_asset_name", label: "Alternate Asset Name" },
    { key: "description", label: "Asset Description" },
    { key: "asset_type", label: "Asset Type" },
    { key: "internal_id", label: "Internal ID" },
    { key: "subsidiary", label: "Subsidiary", wide: true },
  ]},
  { title: "Operations", fields: [
    { key: "operation_status", label: "Operation Status" },
    { key: "asset_status", label: "Asset Status" },
    { key: "asset_class", label: "Class" },
    { key: "location", label: "Location" },
    { key: "department", label: "Department" },
    { key: "driver_name", label: "Driver Name" },
    { key: "coupled_unit", label: "Coupled Unit" },
    { key: "gps_gateway", label: "GPS Gateway" },
    { key: "memo", label: "Asset Memo" },
  ]},
  { title: "Unit Specs", fields: [
    { key: "year_of_manufacturing", label: "Year of Manufacturing", type: "number" },
    { key: "make", label: "Make" },
    { key: "model", label: "Model" },
    { key: "vin_serial", label: "Asset VIN or Serial #" },
    { key: "license_plate", label: "License Plate No" },
    { key: "registration_renewal", label: "Registration Renewal", type: "date" },
  ]},
  { title: "Lease & Finance", fields: [
    { key: "is_leased", label: "Asset Is Leased", type: "checkbox" },
    { key: "monthly_lease_value", label: "Monthly Lease Value", type: "number" },
    { key: "lease_start_date", label: "Lease Start Date", type: "date" },
    { key: "lease_expiration_date", label: "Lease Expiration Date", type: "date" },
    { key: "lienholder", label: "Lienholder" },
    { key: "financed_note_value", label: "Financed Note Value", type: "number" },
  ]},
  { title: "Cost & Depreciation", fields: [
    { key: "purchase_date", label: "Purchase Date", type: "date" },
    { key: "original_cost", label: "Asset Original Cost", type: "number" },
    { key: "current_cost", label: "Asset Current Cost", type: "number" },
    { key: "current_net_book_value", label: "Current Net Book Value", type: "number" },
    { key: "depreciation_active", label: "Depreciation Active", type: "checkbox" },
    { key: "depreciation_method", label: "Depreciation Method" },
    { key: "depreciation_period", label: "Depreciation Period" },
    { key: "asset_lifetime", label: "Asset Lifetime (years)", type: "number" },
    { key: "last_depreciation_date", label: "Last Depreciation Date", type: "date" },
    { key: "depreciation_end_date", label: "Depreciation End Date", type: "date" },
    { key: "disposal_date", label: "Disposal Date", type: "date" },
  ]},
  { title: "Accounts", fields: [
    { key: "asset_account", label: "Asset Account" },
    { key: "liability_account", label: "Liability Account" },
  ]},
];

const NUMERIC_FIELDS = ["year_of_manufacturing", "monthly_lease_value", "financed_note_value", "current_net_book_value", "asset_lifetime", "current_cost", "original_cost"];
const BOOL_FIELDS = ["is_leased", "depreciation_active"];
// Exact import header order (matches the Import CSV template) so an export round-trips into the importer.
const EXPORT_KEYS = [
  "fixed_asset_id", "asset_name", "alt_asset_name", "description", "asset_type", "operation_status", "memo",
  "asset_class", "location", "department", "driver_name", "year_of_manufacturing", "make", "model", "vin_serial",
  "license_plate", "registration_renewal", "coupled_unit", "gps_gateway", "is_leased", "monthly_lease_value",
  "lease_start_date", "lease_expiration_date", "lienholder", "financed_note_value", "liability_account",
  "asset_account", "asset_status", "depreciation_active", "last_depreciation_date", "current_net_book_value",
  "depreciation_end_date", "depreciation_method", "depreciation_period", "asset_lifetime", "purchase_date",
  "current_cost", "original_cost", "disposal_date", "subsidiary", "internal_id",
];
const ALL_KEYS = SECTIONS.flatMap((s) => s.fields.map((f) => f.key));
const EMPTY = Object.fromEntries(
  ALL_KEYS.map((k) => [k, BOOL_FIELDS.includes(k) ? false : k === "operation_status" ? "active" : k === "asset_status" ? "in_service" : k === "asset_type" ? "truck" : ""])
);

const SORT_FIELDS = [
  { value: "fixed_asset_id", label: "ID" },
  { value: "asset_name", label: "Asset Name" },
  { value: "asset_type", label: "Type" },
  { value: "operation_status", label: "Op Status" },
  { value: "asset_class", label: "Class" },
  { value: "location", label: "Location" },
  { value: "department", label: "Department" },
  { value: "driver_name", label: "Driver" },
  { value: "make", label: "Make" },
  { value: "year_of_manufacturing", label: "Year" },
  { value: "original_cost", label: "Orig. Cost" },
  { value: "current_net_book_value", label: "Net Book Value" },
  { value: "asset_status", label: "Asset Status" },
];
const NUMERIC_SORT = ["year_of_manufacturing", "original_cost", "current_net_book_value"];

function assetFormValues(asset) {
  const values = { ...EMPTY };
  ALL_KEYS.forEach((key) => {
    if (asset[key] != null) values[key] = BOOL_FIELDS.includes(key) ? !!asset[key] : asset[key];
  });
  return values;
}

function assetPayload(form) {
  const payload = {};
  ALL_KEYS.forEach((key) => {
    const value = form[key];
    if (BOOL_FIELDS.includes(key)) {
      if (value) payload[key] = true;
    } else if (value !== "" && value != null) {
      payload[key] = NUMERIC_FIELDS.includes(key) ? Number(value) : value;
    }
  });
  return payload;
}

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [filters, setFilters] = useState(EMPTY_ASSET_FILTERS);
  const [sortBy, setSortBy] = useState("asset_name");
  const [sortDir, setSortDir] = useState("asc");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() =>
    api.get("/assets").then((r) => setAssets(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm(assetFormValues(a));
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = assetPayload(form);
    try {
      if (editing) {
        await api.patch(`/assets/${editing.id}`, payload);
        toast.success(`Asset ${form.asset_name} updated`);
      } else {
        await api.post("/assets", payload);
        toast.success(`Asset ${form.asset_name} added to register`);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/assets/${a.id}`, { operation_status: status });
      toast.success(`${a.asset_name} → ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exportAssets = () => {
    const rows = assets.map((a) => EXPORT_KEYS.map((k) =>
      BOOL_FIELDS.includes(k) ? (a[k] === true ? "yes" : a[k] === false ? "no" : "") : (a[k] == null ? "" : a[k])
    ));
    downloadCsvRows(EXPORT_KEYS, rows, `asset_register_${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(rows.length ? `Exported ${rows.length} asset${rows.length === 1 ? "" : "s"} to CSV` : "Exported CSV headers (register is empty)");
  };

  const filteredAssets = useMemo(() => filterAssets(assets, filters), [assets, filters]);
  const sortedAssets = useMemo(() => sortRecords(filteredAssets, sortBy, sortDir, NUMERIC_SORT), [filteredAssets, sortBy, sortDir]);

  return (
    <div data-testid="assets-page" className="min-w-0">
      <PageHeader title="Asset Register" subtitle="Full fixed-asset ledger — identity, lease, finance, depreciation & accounts — shared by all modules" testId="assets-header">
        <ActionButton testId="export-assets-button" variant="secondary" onClick={exportAssets}>
          <Download size={16} /> Export CSV
        </ActionButton>
        <ActionButton testId="import-assets-button" variant="secondary" onClick={() => setImportOpen(true)}>
          <Upload size={16} /> Import CSV
        </ActionButton>
        <ActionButton testId="create-asset-button" onClick={openCreate}>
          <Plus size={16} /> Add Asset
        </ActionButton>
      </PageHeader>

      <AssetFilters assets={assets} filters={filters} setFilters={setFilters} matchCount={filteredAssets.length} />

      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="asset-sort-controls">
        <label htmlFor="asset-sort-field" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400">Sort by</label>
        <SelectInput
          id="asset-sort-field"
          testId="asset-sort-field"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          options={SORT_FIELDS}
          className="!w-48 max-w-full !bg-white !text-slate-100"
        />
        <button
          type="button"
          data-testid="asset-sort-direction"
          onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-xs font-mono uppercase tracking-wider text-slate-200 hover:border-amber-500/50 hover:text-amber-300 transition-colors duration-200"
        >
          {sortDir === "asc" ? <ArrowUpAZ size={14} /> : <ArrowDownAZ size={14} />}
          {sortDir === "asc" ? "A → Z" : "Z → A"}
        </button>
      </div>

      {assets.length === 0 ? (
        <EmptyState message="No assets on the register" testId="assets-empty" />
      ) : sortedAssets.length === 0 ? (
        <EmptyState message="No assets match these filters. Adjust your selections or clear filters." testId="assets-no-matches" />
      ) : (
        <DataTable
          testId="assets-table"
          columns={ASSET_COLUMNS}
          rows={sortedAssets}
          renderRow={(asset) => <AssetRow key={asset.id} asset={asset} setStatus={setStatus} openEdit={openEdit} />}
        />
      )}

      <AssetImport open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      <AssetForm open={open} onOpenChange={setOpen} editing={editing} form={form} setForm={setForm} sections={SECTIONS} submit={submit} busy={busy} />
    </div>
  );
}
